import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all refunds
 */
export const fetchRefunds = createAsyncThunk(
  'refunds/fetchRefunds',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Refunds.app_courses_payments_refund_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch refunds');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch refunds');
    }
  }
);

/**
 * Fetch a single refund by ID
 */
export const fetchRefundById = createAsyncThunk(
  'refunds/fetchRefundById',
  async ({ token, refundId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Refunds.app_courses_payments_refund_get,
        refundId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          ...response.data.data,
          course_title: response.data.data.course_title,
          employee_name: response.data.data.employee_name,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch refund');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch refund');
    }
  }
);

/**
 * Create a new refund
 */
export const createRefund = createAsyncThunk(
  'refunds/createRefund',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });

      const response = await EntityService.addEntity(
        token,
        API.Refunds.app_courses_payments_refund_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create refund');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create refund';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing refund
 */
export const updateRefund = createAsyncThunk(
  'refunds/updateRefund',
  async ({ token, refundId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData[key];

        if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Refunds.app_courses_payments_refund_update,
        refundId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update refund');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update refund';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a refund
 */
export const deleteRefund = createAsyncThunk(
  'refunds/deleteRefund',
  async ({ token, refundId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Refunds.app_courses_payments_refund_delete,
        refundId
      );
      if (response.data.status === 200) {
        return refundId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete refund');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete refund');
    }
  }
);

const initialState = {
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  selectedEntity: null,
  initialFormData: null,
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  formErrors: {},
};

const refundSlice = createSlice({
  name: 'refunds',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRefunds.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchRefunds.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchRefunds.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      })
      .addCase(fetchRefundById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchRefundById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchRefundById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      })
      .addCase(createRefund.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createRefund.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createRefund.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create refund';
        state.formErrors = errorData.errors || {};
      })
      .addCase(updateRefund.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateRefund.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateRefund.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update refund';
        state.formErrors = errorData.errors || {};
      })
      .addCase(deleteRefund.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteRefund.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(refund => refund.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteRefund.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = refundSlice.actions;

export const selectRefunds = (state) => state.refunds.entities;
export const selectRefundsPagination = (state) => state.refunds.pagination;
export const selectSelectedRefund = (state) => state.refunds.selectedEntity;
export const selectInitialFormData = (state) => state.refunds.initialFormData;
export const selectRefundsListStatus = (state) => state.refunds.listStatus;
export const selectRefundDetailStatus = (state) => state.refunds.detailStatus;
export const selectRefundCreateStatus = (state) => state.refunds.createStatus;
export const selectRefundUpdateStatus = (state) => state.refunds.updateStatus;
export const selectRefundDeleteStatus = (state) => state.refunds.deleteStatus;
export const selectRefundsListError = (state) => state.refunds.listError;
export const selectRefundDetailError = (state) => state.refunds.detailError;
export const selectRefundCreateError = (state) => state.refunds.createError;
export const selectRefundUpdateError = (state) => state.refunds.updateError;
export const selectRefundDeleteError = (state) => state.refunds.deleteError;
export const selectRefundFormErrors = (state) => state.refunds.formErrors;

export default refundSlice.reducer;
